import {Map as MapSDK} from '@maptiler/sdk'
import {createContext, useEffect, useMemo, useRef, useState, forwardRef, memo, useCallback} from 'react'
import PropTypes from 'prop-types'

export const MapContext = createContext(undefined)

const MapComponent = forwardRef(
  (
    {
      children,
      initialize: initializeProp = true,
      interactiveLayerIds = [],
      containerStyle = {},
      onLoad,
      onClick,
      onMouseMove,
      onMouseLeave,
      onMouseOut,
      onMoveEnd,
      ...props
    },
    ref
  ) => {
    const [initialize, setInitialize] = useState(initializeProp)
    const [styleLoaded, setStyleLoaded] = useState(false)
    const [loaded, setLoaded] = useState(false)

    const map = useRef(null)
    const mapContainer = useRef(null)

    // Initialize the map instance
    const initializeMap = useCallback(() => {
      if (map.current) return
      setInitialize(true)
    }, [])

    // Attach and detach event listeners
    const attachEventListener = useCallback((event, handler) => {
      if (!map.current || !handler) return
      map.current.on(event, handler)
      return () => {
        if (map.current) {
          map.current.off(event, handler)
        }
      }
    }, [])

    useEffect(() => {
      if (map.current || !mapContainer.current || !initialize) return
      const newMap = new MapSDK({
        container: mapContainer.current,
        ...props
      })
      newMap.once('style.load', () => setStyleLoaded(true))
      newMap.once('load', () => setLoaded(true))
      newMap.getCanvas().style.cursor = 'default'

      // disable map rotation using right click + drag
      newMap.dragRotate.disable()

      // disable map rotation using keyboard
      newMap.keyboard.disable()

      // disable map rotation using touch rotation gesture
      newMap.touchPitch.disable()

      // disable map rotation using touch rotate
      newMap.touchZoomRotate.disableRotation()

      map.current = newMap

      if (ref) {
        if (typeof ref === 'function') {
          ref(newMap)
        } else {
          ref.current = newMap
        }
      }
    }, [initialize, props, ref])

    useEffect(() => attachEventListener('click', onClick), [onClick, attachEventListener])
    useEffect(() => attachEventListener('load', onLoad), [onLoad, attachEventListener])
    useEffect(() => attachEventListener('mousemove', onMouseMove), [onMouseMove, attachEventListener])
    useEffect(() => attachEventListener('mouseout', onMouseOut), [onMouseOut, attachEventListener])
    useEffect(() => attachEventListener('moveend', onMoveEnd), [onMoveEnd, attachEventListener])

    useEffect(() => {
      if (!map.current || !onMouseLeave || interactiveLayerIds.length === 0) return

      for (const layerId of interactiveLayerIds) {
        map.current.on('mouseleave', layerId, onMouseLeave)
      }

      return () => {
        for (const layerId of interactiveLayerIds) {
          map.current.off('mouseleave', layerId, onMouseLeave)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onMouseLeave])

    const styles = useMemo(
      () => ({
        position: 'absolute',
        width: '100%',
        height: '100%',
        ...containerStyle
      }),
      [containerStyle]
    )

    const contextValue = useMemo(
      () => ({map, styleLoaded, loaded, initializeMap}),
      [styleLoaded, loaded, initializeMap]
    )

    return (
      <div style={styles} ref={mapContainer}>
        {map.current && (
          <MapContext.Provider value={contextValue}>
            <div style={{height: '100%'}}>{children}</div>
          </MapContext.Provider>
        )}
      </div>
    )
  }
)

MapComponent.displayName = 'Map'

MapComponent.propTypes = {
  initialize: PropTypes.bool,
  interactiveLayerIds: PropTypes.arrayOf(PropTypes.string),
  containerStyle: PropTypes.object,
  onLoad: PropTypes.func,
  children: PropTypes.node,
  onClick: PropTypes.func,
  onMouseMove: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseOut: PropTypes.func,
  onMoveEnd: PropTypes.func
}

/**
 * Props for the Map component.
 * @typedef {Object} MapComponentExtraProps
 * @property {boolean} [initialize] - Whether to initialize the map or not. Default is true.
 * @property {string[]} [interactiveLayerIds] - Array of layer IDs that should be interactive.
 * @property {React.CSSProperties} [containerStyle] - Style object for the map container.
 * @property {function} [onLoad] - Callback function for when the map is loaded.
 * @property {React.ReactNode} [children] - Child components to be rendered inside the map.
 * @typedef {Object} MapMouseEvents - Mouse event callbacks for the map.Memoise the callbacks and dependencies to avoid re renders
 * @property {function} [onClick] - Callback function for click events on the map.Memoise the callbacks and dependencies to avoid re renders
 * @property {function} [onMouseMove] - Callback function for mouse move events on the map.Memoise the callbacks and dependencies to avoid re renders
 * @property {function} [onMouseLeave] - Callback function for mouse leave events on the map.Memoise the callbacks and dependencies to avoid re renders
 * @property {function} [onMouseOut] - Callback function for mouse out events on the map.Memoise the callbacks and dependencies to avoid re renders
 *
 * @typedef {import('@maptiler/sdk').MapOptions & MapComponentExtraProps & MapMouseEvents} MapComponentProps
 */
/**
 
/**
 * A wrapper component for the Maptiler map that manages the lifecycle of the map instance.
 * @type React.ForwardRefRenderFunction<HTMLButtonElement, MapComponentProps>
 */

export default memo(MapComponent)

/**
 * List of functions and their purpose:
 *
 * - MapComponent: Manages the lifecycle of a Maptiler map instance and renders child components within the map context.
 * - initializeMap: Initializes the map instance if it hasn't been created yet.
 * - attachEventListener: Attaches and detaches event listeners for the map.
 */
